/* eslint-disable import/no-extraneous-dependencies -- Reason: legacy suppression retained until the owning code is migrated or cleaned up. */
import dayjs from '@tt/dates/dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import qs from 'qs';

import { useManagementStore, userStore, useSettingStore } from '../store';
import axios from './axios';
import getAggregateData from './market-data';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const { brokers, profitCalculationMethods } = require('../resources/static/select-input-data');

const fetchLogs = (accountId) => axios.get(`crud/logs/${accountId}`).then(({ data }) => data);

/* FORMAT STRINGS, DATES, ETC */
export const FORMAT = {
    CURRENCY_MEASURE() {
        return useSettingStore.getState().filter.basic_measure;
    },
    TRADE_BEAUTIFY(o, timezone) {
        let realizedRMultiple;

        if (o.status === 'open') {
            realizedRMultiple = '-';
        } else {
            realizedRMultiple = o.realized_R_multiple ? `${o.realized_R_multiple.toFixed(2)}R` : '';
        }

        // MFE (renamed from farthestMovement) - supports both old and new field names for backwards compatibility
        let mfeValue;
        if (o.mfe) {
            mfeValue = this.NUMBER_TO_CURRENCY(o.mfe);
        } else if (o.farthestMovement) {
            mfeValue = this.NUMBER_TO_CURRENCY(o.farthestMovement);
        } else {
            mfeValue = 'N/A';
        }

        const tradeObj = {
            ...o,
            openTime: this.DATE_AND_TIME_WITH_FORMAT(o.openTime, timezone),
            closeTime:
                o.closeTime !== null ? this.DATE_AND_TIME_WITH_FORMAT(o.closeTime, timezone) : '',
            openPrice: this.NUMBER_TO_CURRENCY(o.openPrice),
            closePrice: o.status === 'open' ? '-' : this.NUMBER_TO_CURRENCY(o.closePrice),
            mfe: mfeValue,
            theoreticalMfe: o.theoreticalMfe ? this.NUMBER_TO_CURRENCY(o.theoreticalMfe) : 'N/A',
            mae: o.mae ? this.NUMBER_TO_CURRENCY(o.mae) : 'N/A',
            // Backwards compatibility alias
            farthestMovement: mfeValue,
            executions: o.executions.map((e, index) =>
                this.EXECUTION_BEAUTIFY(e, index, o.executions.length, timezone)
            ),
            netPnL: this.NUMBER_TO_CURRENCY(o.netPnL),
            totalVolume: o.status === 'open' ? '-' : o.totalVolume,
            totalCommission: this.NUMBER_TO_CURRENCY(o.totalCommission),
            optionContract: o.assetType === 'Option' ? this.DECOMPOSE_OPTION_STRING(o.symbol) : '',
            initialTarget: o.status === 'open' ? '-' : this.NUMBER_TO_CURRENCY(o.initial_target),
            tradeRisk: o.status === 'open' ? '-' : this.NUMBER_TO_CURRENCY(o.trade_risk),
            plannedRMultiple: o.planned_R_multiple ? `${o.planned_R_multiple.toFixed(2)}R` : '',
            realizedRMultiple
        };

        if (this.CURRENCY_MEASURE() === 'R-multiple') {
            return {
                ...tradeObj,
                netPnL: this.NUMBER_TO_R_MULTIPLE(o.realized_R_multiple)
            };
        }

        return tradeObj;
    },
    ACCOUNTS_BEAUTIFY(o) {
        return {
            // ...o,
            liveAccount: o.liveAccount,
            pct_account: o.pct_account,
            ptt_account: o.ptt_account,
            simulator_account: o.simulator_account,
            unassigned_view: Boolean(o.unassigned_view),
            key: o._id,
            label: o.name,
            value: o._id,
            broker: o.broker,
            brokerLabel: this.BROKER_ID_TO_LABEL(o.broker),
            brokerTimezone: o.brokerTimezone,
            profitCalculationMethod: this.PROFIT_CALCULATION_METHOD_ID_TO_LABEL(
                o.profitCalculationMethod
            ),
            numberOfTrades: o.numberOfTrades,
            notes: o.notes
        };
    },
    EXECUTION_BEAUTIFY(o, index, length, timezone) {
        return {
            ...o,
            date: this.DATE_AND_TIME_WITH_FORMAT(o.date, timezone),
            price: this.NUMBER_TO_CURRENCY(o.price),
            commission: this.NUMBER_TO_CURRENCY(o.commission),
            realizedGain: this.NUMBER_TO_CURRENCY(o.realizedGain),
            netRealizedGain: this.NUMBER_TO_CURRENCY(o.netRealizedGain),
            mark: this.EXECUTION_MARK_MAP(o, index, length, timezone)
        };
    },
    EXECUTION_MARK_MAP(o, index, length, timezone) {
        if (index === 0) {
            return {
                id: index,
                time: dayjs(o.date).tz(timezone).valueOf() / 1000,
                side: o.side.toLowerCase(),
                color: o.side === 'SELL' ? '#E57373' : '#29a373',
                shape: o.side === 'SELL' ? 'arrow_down' : 'arrow_up',
                price: o.price,
                text: `OPEN - ${o.side} @ ${o.price}`,
                textColor: '#000',
                overrides: {
                    fontsize: 14,
                    color: '#000'
                }
            };
        }
        if (index === length - 1 && o.remainingShares === 0) {
            return {
                id: index,
                time: dayjs(o.date).valueOf() / 1000,
                side: o.side.toLowerCase(),
                color: o.side === 'SELL' ? '#E57373' : '#29a373',
                shape: o.side === 'SELL' ? 'arrow_down' : 'arrow_up',
                price: o.price,
                text: `CLOSE - ${o.side} @ ${o.price}`,
                textColor: '#000',
                overrides: {
                    fontsize: 14,
                    color: '#000'
                }
            };
        }

        return {
            id: index,
            time: dayjs(o.date).valueOf() / 1000,
            side: o.side.toLowerCase(),
            color: o.side === 'SELL' ? '#E57373' : '#29a373',
            shape: o.side === 'SELL' ? 'icon' : 'icon',
            price: o.price,
            text: `${o.side} @ ${o.price}`,
            textColor: '#000',
            overrides: {
                icon: o.side === 'SELL' ? 0xf0d7 : 0xf0d8,
                size: 20,
                color: o.side === 'SELL' ? '#E57373' : '#29a373'
            }
        };
    },
    SUMMARY_BEAUTIFY(o) {
        // START Separate into negative and positve data arrays
        const separatedPositiveNegativePnL = [[], []];
        const formattedPnL = [];

        o.PnLPerDayAccumulated.forEach((i) => {
            if (i.totalAccumulatedPnLPerDay >= 0) {
                separatedPositiveNegativePnL[0].push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
                separatedPositiveNegativePnL[1].push({
                    x: i.date,
                    y: null
                });

                formattedPnL.push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
            } else {
                separatedPositiveNegativePnL[1].push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
                separatedPositiveNegativePnL[0].push({
                    x: i.date,
                    y: null
                });

                formattedPnL.push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
            }
        });
        // END Separate into negative and positve data arrays

        if (this.CURRENCY_MEASURE() === 'R-multiple') {
            return {
                ...o,
                netPnL: this.NUMBER_TO_R_MULTIPLE(o.netPnLRMultiple),
                avgPnL: this.NUMBER_TO_R_MULTIPLE(o.avgPnLRMultiple),
                avgPnLPerDay: this.NUMBER_TO_CURRENCY(o.avgPnLPerDay),
                avgPnLPerMonth: this.NUMBER_TO_CURRENCY(o.avgPnLPerMonth),
                avgPnLPerYear: this.NUMBER_TO_CURRENCY(o.avgPnLPerYear),
                avgTradesPerDay: o.avgTradesPerDay ? o.avgTradesPerDay.toFixed(2) : '',
                avgTradesPerMonth: o.avgTradesPerMonth ? o.avgTradesPerMonth.toFixed(2) : '',
                avgTradesPerYear: o.avgTradesPerYear ? o.avgTradesPerYear.toFixed(2) : '',
                maxPnL: this.NUMBER_TO_R_MULTIPLE(o.maxPnLRMultiple),
                minPnL: this.NUMBER_TO_R_MULTIPLE(o.minPnLRMultiple),
                maxPnLPerDay: this.NUMBER_TO_CURRENCY(o.maxPnLPerDay),
                maxPnLPerMonth: this.NUMBER_TO_CURRENCY(o.maxPnLPerMonth),
                maxPnLPerYear: this.NUMBER_TO_CURRENCY(o.maxPnLPerYear),
                minPnLPerDay: this.NUMBER_TO_CURRENCY(o.minPnLPerDay),
                minPnLPerMonth: this.NUMBER_TO_CURRENCY(o.minPnLPerMonth),
                mostProfitableByTrades: {
                    ...o.mostProfitableByTrades,
                    symbol:
                        o.mostProfitableByTrades.wins !== 0 ? o.mostProfitableByTrades.symbol : ''
                },
                avgExecutions: o.avgExecutions ? o.avgExecutions.toFixed(2) : '',
                minPnLPerYear: this.NUMBER_TO_CURRENCY(o.minPnLPerYear),
                mostProfitableByPnL: {
                    symbol: o.mostProfitableByPnLRMultiple.symbol,
                    PnL: this.NUMBER_TO_R_MULTIPLE(o.mostProfitableByPnLRMultiple.PnL)
                },
                leastProfitableByPnL: {
                    symbol: o.leastProfitableByPnLRMultiple.symbol,
                    PnL: this.NUMBER_TO_R_MULTIPLE(o.leastProfitableByPnLRMultiple.PnL)
                },
                leastProfitableByTrades: {
                    ...o.leastProfitableByTrades,
                    symbol:
                        o.leastProfitableByTrades.losses !== 0
                            ? o.leastProfitableByTrades.symbol
                            : ''
                },
                PnLPerDayAccumulated: separatedPositiveNegativePnL,
                PnLPerDayAccumulatedFormatted: formattedPnL,
                winLossRatio: o.winLossRatio ? o.winLossRatio.toFixed(2) : '',
                totalCommissions: this.NUMBER_TO_CURRENCY(o.totalCommissions)
            };
        }

        return {
            ...o,
            netPnL: this.NUMBER_TO_CURRENCY(o.netPnL),
            avgPnL: this.NUMBER_TO_CURRENCY(o.avgPnL),
            avgPnLPerDay: this.NUMBER_TO_CURRENCY(o.avgPnLPerDay),
            avgPnLPerMonth: this.NUMBER_TO_CURRENCY(o.avgPnLPerMonth),
            avgPnLPerYear: this.NUMBER_TO_CURRENCY(o.avgPnLPerYear),
            avgTradesPerDay: o.avgTradesPerDay ? o.avgTradesPerDay.toFixed(2) : '',
            avgTradesPerMonth: o.avgTradesPerMonth ? o.avgTradesPerMonth.toFixed(2) : '',
            avgTradesPerYear: o.avgTradesPerYear ? o.avgTradesPerYear.toFixed(2) : '',
            maxPnL: this.NUMBER_TO_CURRENCY(o.maxPnL),
            minPnL: this.NUMBER_TO_CURRENCY(o.minPnL),
            maxPnLPerDay: this.NUMBER_TO_CURRENCY(o.maxPnLPerDay),
            maxPnLPerMonth: this.NUMBER_TO_CURRENCY(o.maxPnLPerMonth),
            maxPnLPerYear: this.NUMBER_TO_CURRENCY(o.maxPnLPerYear),
            minPnLPerDay: this.NUMBER_TO_CURRENCY(o.minPnLPerDay),
            minPnLPerMonth: this.NUMBER_TO_CURRENCY(o.minPnLPerMonth),
            mostProfitableByTrades: {
                ...o.mostProfitableByTrades,
                symbol: o.mostProfitableByTrades.wins !== 0 ? o.mostProfitableByTrades.symbol : ''
            },
            avgExecutions: o.avgExecutions ? o.avgExecutions.toFixed(2) : '',
            minPnLPerYear: this.NUMBER_TO_CURRENCY(o.minPnLPerYear),
            mostProfitableByPnL:
                o.mostProfitableByPnL.PnL > 0
                    ? {
                          symbol: o.mostProfitableByPnL.symbol,
                          PnL: this.NUMBER_TO_CURRENCY(o.mostProfitableByPnL.PnL)
                      }
                    : { symbol: null, PnL: '-' },
            leastProfitableByPnL:
                o.leastProfitableByPnL.PnL < 0
                    ? {
                          symbol: o.leastProfitableByPnL.symbol,
                          PnL: this.NUMBER_TO_CURRENCY(o.leastProfitableByPnL.PnL)
                      }
                    : { symbol: null, PnL: '-' },
            leastProfitableByTrades: {
                ...o.leastProfitableByTrades,
                symbol:
                    o.leastProfitableByTrades.losses !== 0 ? o.leastProfitableByTrades.symbol : ''
            },
            PnLPerDayAccumulated: separatedPositiveNegativePnL,
            PnLPerDayAccumulatedFormatted: formattedPnL,
            winLossRatio: o.winLossRatio ? o.winLossRatio.toFixed(2) : '',
            totalCommissions: this.NUMBER_TO_CURRENCY(o.totalCommissions)
        };
    },
    INDIVIDUAL_ACCOUNT_ADMIN_BEAUTIFY(o) {
        // START Separate into negative and positve data arrays
        const separatedPositiveNegativePnL = [[], []];
        const formattedPnL = [];

        o.equityCurve.forEach((i) => {
            if (i.totalAccumulatedPnLPerDay >= 0) {
                separatedPositiveNegativePnL[0].push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
                separatedPositiveNegativePnL[1].push({
                    x: i.date,
                    y: null
                });

                formattedPnL.push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
            } else {
                separatedPositiveNegativePnL[1].push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
                separatedPositiveNegativePnL[0].push({
                    x: i.date,
                    y: null
                });

                formattedPnL.push({
                    x: i.date,
                    y: i.totalAccumulatedPnLPerDay
                });
            }
        });
        // END Separate into negative and positve data arrays

        if (this.CURRENCY_MEASURE() === 'R-multiple') {
            return {
                ...o,
                equityCurve: separatedPositiveNegativePnL,
                equityCurveFormatted: formattedPnL
            };
        }

        return {
            ...o,
            equityCurve: separatedPositiveNegativePnL,
            equityCurveFormatted: formattedPnL
        };
    },
    COMPARE_TABLE_BEAUTIFY(o) {
        // Mapping of original keys to desired names and formatting
        const keyMapping = {
            winPercentage: {
                newKey: 'Win %',
                formatter: this.TWO_DECIMALS,
                winnerCondition: 'max'
            },
            averageNetPnLWin: {
                newKey: 'Average Winner',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            averageNetPnLLoss: {
                newKey: 'Average Loser',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            avgTradesPerDay: {
                newKey: 'Average Trades /Day',
                formatter: this.TWO_DECIMALS,
                winnerCondition: 'max'
            },
            avgWinnerLoser: {
                newKey: 'Average Winner /Loser',
                formatter: this.TWO_DECIMALS,
                winnerCondition: 'max'
            },
            totalNetPnL: {
                newKey: 'Total P&L',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            maxNetPnL: {
                newKey: 'Max P&L',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            minNetPnL: {
                newKey: 'Min P&L',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            averageNetPnL: {
                newKey: 'Average P&L',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            wins: {
                newKey: 'Wins',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            losses: {
                newKey: 'Losses',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'min'
            },
            breakeven: {
                newKey: 'Breakeven',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            long: {
                newKey: 'Long',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            short: {
                newKey: 'Short',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalTrades: {
                newKey: 'Total Trades',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalClosedTrades: {
                newKey: 'Total Closed Trades',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalOpenTrades: {
                newKey: 'Total Open Trades',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            maxExecutionsPerTrade: {
                newKey: 'Max Executions Per Trade',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            minExecutionsPerTrade: {
                newKey: 'Min Executions Per Trade',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalExecutions: {
                newKey: 'Total Executions',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalVolume: {
                newKey: 'Total Volume',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalVolumeClosedTrades: {
                newKey: 'Total Volume Closed Trades',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalVolumeOpenTrades: {
                newKey: 'Total Volume Open Trades',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            averageExecutionsPerTrade: {
                newKey: 'Average Executions Per Trade',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            averageVolume: {
                newKey: 'Average Volume',
                formatter: this.TWO_DECIMALS,
                winnerCondition: 'max'
            },
            maxVolume: {
                newKey: 'Max Volume',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            minVolume: {
                newKey: 'Min Volume',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalVolumeWin: {
                newKey: 'Total Volume Win',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            totalVolumeLoss: {
                newKey: 'Total Volume Loss',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            averageVolumeWin: {
                newKey: 'Average Volume Win',
                formatter: this.TWO_DECIMALS,
                winnerCondition: 'max'
            },
            averageVolumeLoss: {
                newKey: 'Average Volume Loss',
                formatter: this.TWO_DECIMALS,
                winnerCondition: 'max'
            },
            totalCommissionsAndFees: {
                newKey: 'Total Commissions And Fees',
                formatter: this.NUMBER_TO_CURRENCY,
                winnerCondition: 'max'
            },
            firstTrade: { newKey: 'First Trade', winnerCondition: 'max' },
            longestTradeDuration: {
                newKey: 'Longest Trade Duration',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            fastestTradeDuration: {
                newKey: 'Fastest Trade Duration',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            longestOpenTradeDuration: {
                newKey: 'Longest Open Trade Duration',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            longestWinningTradeDuration: {
                newKey: 'Longest Winning Trade Duration',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            longestLosingTradeDuration: {
                newKey: 'Longest Losing Trade Duration',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            shortestOpenTradeDuration: {
                newKey: 'Shortest Open Trade Duration',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            averageDurationPerTrade: {
                newKey: 'Average Duration Per Trade',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            averageDurationPerWinningTrade: {
                newKey: 'Average Duration Per Winning Trade',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            averageDurationPerLosingTrade: {
                newKey: 'Average Duration Per Losing Trade',
                winnerCondition: 'max',
                formatter: this.HUMANIZE_DURATION
            },
            maxConsecutiveWins: {
                newKey: 'Max Consecutive Wins',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'max'
            },
            maxConsecutiveLosses: {
                newKey: 'Max Consecutive Losses',
                formatter: this.REMOVE_DECIMALS,
                winnerCondition: 'min'
            }
        };

        const renamedItem = [];
        // eslint-disable-next-line no-unused-vars -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
        Object.entries(keyMapping).forEach(([key, value], index) => {
            const v = o.find((i) => i.key === key);
            if (!v) return;

            const { newKey } = value;
            const formatFunction = value.formatter;
            renamedItem[index] = {
                key: newKey,
                groupA: formatFunction ? formatFunction(v.groupA) : v.groupA,
                groupB: formatFunction ? formatFunction(v.groupB) : v.groupB,
                groupC: formatFunction ? formatFunction(v.groupC) : v.groupC
            };

            // Find the maximum value among all groups
            const winnerOverallValue =
                value.winnerCondition === 'max'
                    ? Math.max(v.groupA, v.groupB, v.groupC)
                    : Math.min(v.groupA, v.groupB, v.groupC);

            const winnerGroups = [];
            if (v.groupA === winnerOverallValue) winnerGroups.push('groupA');
            if (v.groupB === winnerOverallValue) winnerGroups.push('groupB');
            if (v.groupC === winnerOverallValue) winnerGroups.push('groupC');

            renamedItem[index].winner = winnerGroups;
        });

        return renamedItem;
    },
    DATE_AND_TIME_WITH_FORMAT(date, timezone) {
        return dayjs(date).tz(timezone).format('YYYY-MM-DD HH:mm:ss');
    },
    DATE_AND_TIME(date, timezone) {
        return dayjs(date).tz(timezone);
    },
    BROKER_ID_TO_LABEL(id) {
        return brokers.find((o) => o.value === id).label;
    },
    PROFIT_CALCULATION_METHOD_ID_TO_LABEL(id) {
        return profitCalculationMethods.find((o) => o.value === id).label;
    },
    NUMBER_TO_CURRENCY(value) {
        if (value === null || value === undefined) {
            return '';
        }

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    },
    NUMBER_TO_R_MULTIPLE(value) {
        if (value === null || value === undefined) {
            return '';
        }

        return `${value.toFixed(2)}R`;
    },
    CURRENCY_TO_NUMBER(currency) {
        try {
            if (currency === null || currency === undefined) {
                return '';
            }

            if (this.CURRENCY_MEASURE() === 'R-multiple') {
                // Extract the numeric part and convert it to a number
                return Number(currency?.replace('R', '')) || 0;
            }

            // For other cases, remove non-numeric characters and convert to a number
            return Number(currency?.replace(/[^0-9.-]+/g, '')) || 0;
        } catch (error) {
            return '';
        }
    },
    DECOMPOSE_OPTION_STRING(str) {
        if (!str) return {};

        const parts = str.split(' '); // Split the input string by space
        if (parts.length !== 4) {
            throw new Error('Invalid option string format');
        }

        const underlying = parts[0]; // Underlying symbol
        const type = parts[1]; // Option type (CALL or PUT)
        const strike = parseFloat(parts[2]); // Option strike price as a float
        const date = parts[3]; // Option expiration date

        return {
            underlying,
            type,
            strike,
            date
        };
    },
    REMOVE_DECIMALS(value) {
        // Check if the value is a number
        if (typeof value === 'number' && !Number.isNaN(value)) {
            // Round down to the nearest integer using Math.floor()
            return Math.floor(value);
        }
        // Return the value as is if it's not a number or is NaN
        return value;
    },
    TWO_DECIMALS(value) {
        // Check if the value is a number
        if (typeof value === 'number' && !Number.isNaN(value)) {
            // Round down to the nearest integer using Math.floor()
            return value.toFixed(2);
        }
        // Return the value as is if it's not a number or is NaN
        return value;
    },
    HUMANIZE(value, fn) {
        if (!value && value !== 0) {
            return '0';
        }
        const ALPHABET = 'KMBTPEZY'.split('');
        const THRESHOLD = 1e3;

        let n = Math.abs(value);
        let index = 0;
        while (n >= THRESHOLD && index + 1 < ALPHABET.length) {
            n /= THRESHOLD;
            index += 1;
        }
        if (fn) n = fn(n);
        return (value < 0 ? '-' : '') + String(index === 0 ? n : n + ALPHABET[index - 1]);
    },
    HUMANIZE_DURATION(value) {
        if (!value && value !== 0) {
            return '';
        }
        if (value === 0) {
            return '0';
        }
        return dayjs.duration(value, 'seconds').humanize();
    }
};

export const LOGS = {
    processLogs: (accounts, setAllAccounts) => {
        const merged = [];

        if (accounts && accounts.length > 0) {
            fetchLogs(accounts.map((account) => account.key)).then((data) => {
                // Merge Accounts and Logs
                for (let i = 0; i < accounts.length; i += 1) {
                    const logsAssociatedWithAccount = data.filter(
                        (itmInner) => itmInner.accountRef === accounts[i].key
                    );

                    merged.push({
                        ...accounts[i],
                        executionsLogs: logsAssociatedWithAccount,
                        numberLogFiles: logsAssociatedWithAccount.length
                    });
                }

                setAllAccounts(merged);

                return merged;
            });
        } else if (accounts && accounts.length === 0) {
            setAllAccounts([]);
        }

        return merged;
    }
};

export const TRADES = {
    fetchTrades: (filter, allAccounts) =>
        axios
            .get('crud/trades/filter', {
                params: {
                    accounts: filter.accounts,
                    tags: filter.tags,
                    showAllTags: filter.showAllTags,
                    symbols: filter.symbols,
                    status: filter.status,
                    side: filter.side,
                    startDate: filter.dateRange
                        ? filter.dateRange[0].toString()
                        : dayjs()
                              .add(-20, 'y')
                              .set('hour', 0)
                              .set('minute', 0)
                              .set('second', 0)
                              .set('millisecond', 0)
                              .toDate(),
                    endDate: filter.dateRange
                        ? filter.dateRange[1].toString()
                        : dayjs()
                              .add(20, 'y')
                              .set('hour', 23)
                              .set('minute', 59)
                              .set('second', 59)
                              .set('millisecond', 59)
                              .toDate(),
                    assetType: filter.assetType,
                    getAllofTickers: filter.getAllofTickers,
                    // TradeBook filters
                    tradingPlanId: filter.tradingPlanId || undefined,
                    strategySetType: filter.strategySetType || undefined,
                    setNumber: filter.setNumber !== null ? filter.setNumber : undefined
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
            })
            .then((response) => {
                let responseData = null;

                if (response.status === 200) {
                    responseData = response.data.map((trade) => {
                        const accountTimeZone = allAccounts.find(
                            (a) => a.key === trade.accountRef
                        )?.brokerTimezone;
                        return FORMAT.TRADE_BEAUTIFY(trade, accountTimeZone);
                    });
                }

                return responseData;
            })
            .catch((error) => {
                console.error('Error:', error);
            })
};

export const API = {
    getTickers: async () =>
        axios
            .get('https://tt-static-files.s3.us-west-2.amazonaws.com/tickers.json.gz')
            .then(({ data }) => data),
    getFullSymbolList: async () =>
        axios
            .get('api/full-symbol-list', {
                params: {
                    type: 'stocks'
                }
            })
            .then(({ data }) => data.symbols),
    getAggregateData
};

export const SUMMARY = {
    fetchSummary: (filter) =>
        axios
            .get('stats/summary', {
                params: {
                    accounts: filter.accounts,
                    assetType: filter.assetType,
                    tags: filter.tags,
                    showAllTags: filter.showAllTags,
                    symbols: filter.symbols,
                    status: filter.status,
                    side: filter.side,
                    startDate: filter.dateRange
                        ? filter.dateRange[0].toString()
                        : dayjs()
                              .add(-20, 'y')
                              .set('hour', 0)
                              .set('minute', 0)
                              .set('second', 0)
                              .set('millisecond', 0)
                              .toDate(),
                    endDate: filter.dateRange
                        ? filter.dateRange[1].toString()
                        : dayjs()
                              .add(20, 'y')
                              .set('hour', 23)
                              .set('minute', 59)
                              .set('second', 59)
                              .set('millisecond', 59)
                              .toDate(),
                    getAllofTickers: filter.getAllofTickers
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
            })
            .then((response) => {
                let responseData = null;

                if (response.status === 200) {
                    responseData = FORMAT.SUMMARY_BEAUTIFY(response.data);
                }

                return responseData;
            })
            .catch((error) => {
                console.error('Error:', error);
                return null;
            }),
    getUserByPCTId: async (id) => axios.get(`crud/users/${id}`).then(({ data }) => data),

    fetchEquityCurveDefaults: (filter) =>
        axios
            .get('stats/equity-curve-defaults', {
                params: {
                    accounts: filter.accounts,
                    assetType: filter.assetType,
                    tags: filter.tags,
                    showAllTags: filter.showAllTags,
                    symbols: filter.symbols,
                    status: filter.status,
                    side: filter.side,
                    startDate: filter.dateRange
                        ? filter.dateRange[0].toString()
                        : dayjs()
                              .add(-20, 'y')
                              .set('hour', 0)
                              .set('minute', 0)
                              .set('second', 0)
                              .set('millisecond', 0)
                              .toDate(),
                    endDate: filter.dateRange
                        ? filter.dateRange[1].toString()
                        : dayjs()
                              .add(20, 'y')
                              .set('hour', 23)
                              .set('minute', 59)
                              .set('second', 59)
                              .set('millisecond', 59)
                              .toDate(),
                    getAllofTickers: filter.getAllofTickers
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
            })
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }

                return null;
            })
            .catch((error) => {
                if (error.response?.status === 204) {
                    return null;
                }
                console.error('Error fetching equity curve defaults:', error);
                return null;
            })
};

export const ADMIN = {
    fetchPaidFundedAccountsLeaderboard: ({
        page = 1,
        pageSize = 25,
        sortBy = 'paidDate',
        sortOrder = 'desc',
        period = 'all_time'
    } = {}) =>
        axios
            .get('crud/public/funded-accounts/leaderboard', {
                params: { page, pageSize, sortBy, sortOrder, period }
            })
            .then(({ data }) => {
                let periodLabel = 'All Time';
                if (period === 'last_30_days') {
                    periodLabel = 'Last 30 Days';
                } else if (period === 'current_traders') {
                    periodLabel = 'Current Traders';
                }

                const buildTopRowsFromRows = (sourceRows = []) =>
                    [...sourceRows]
                        .filter((row) => Number(row?.pnl || 0) > 0)
                        .sort((a, b) => {
                            const pnlDiff = Number(b?.pnl || 0) - Number(a?.pnl || 0);
                            if (pnlDiff !== 0) return pnlDiff;
                            return (
                                new Date(b?.closedPaidAt || 0).getTime() -
                                new Date(a?.closedPaidAt || 0).getTime()
                            );
                        })
                        .slice(0, 10)
                        .map((row, index) => ({
                            ...row,
                            rank: row?.rank || index + 1
                        }));

                if (!data || Array.isArray(data)) {
                    const safeRows = Array.isArray(data) ? data : [];
                    const topRows = buildTopRowsFromRows(safeRows);
                    const fallbackTotalPayoutPaid = safeRows.reduce((totalPayout, row) => {
                        const payoutValue = Number(row?.payoutAmount);
                        if (Number.isFinite(payoutValue)) return totalPayout + payoutValue;

                        const pnlValue = Number(row?.pnl);
                        return Number.isFinite(pnlValue) ? totalPayout + pnlValue : totalPayout;
                    }, 0);

                    return {
                        page: 1,
                        pageSize,
                        total: safeRows.length,
                        totalPages: safeRows.length ? 1 : 0,
                        topLeaderboard: {
                            period,
                            periodLabel,
                            maxPnl: topRows[0]?.pnl || 0,
                            topTrader: topRows[0] || null,
                            stats: {
                                paidAccountsCount: safeRows.length,
                                totalPayoutPaid: fallbackTotalPayoutPaid
                            },
                            rows: topRows
                        },
                        rows: safeRows
                    };
                }

                const safeRows = Array.isArray(data?.rows) ? data.rows : [];
                const apiTopLeaderboard = data?.topLeaderboard || {};
                const apiTopRows = Array.isArray(apiTopLeaderboard?.rows)
                    ? apiTopLeaderboard.rows
                    : [];
                const resolvedTopRows =
                    apiTopRows.length > 0 ? apiTopRows : buildTopRowsFromRows(safeRows);

                return {
                    ...data,
                    page: data?.page || page,
                    pageSize: data?.pageSize || pageSize,
                    sortBy: data?.sortBy || sortBy,
                    sortOrder: data?.sortOrder || sortOrder,
                    total: data?.total || 0,
                    totalPages: data?.totalPages || 0,
                    rows: safeRows,
                    topLeaderboard: {
                        ...apiTopLeaderboard,
                        period: apiTopLeaderboard?.period || period,
                        periodLabel: apiTopLeaderboard?.periodLabel || periodLabel,
                        maxPnl: apiTopLeaderboard?.maxPnl || resolvedTopRows?.[0]?.pnl || 0,
                        topTrader: apiTopLeaderboard?.topTrader || resolvedTopRows?.[0] || null,
                        stats: apiTopLeaderboard?.stats || {},
                        rows: resolvedTopRows
                    }
                };
            }),
    fetchAccounts: (token) =>
        axios.get('crud/funded-accounts', { headers: { token } }).then(({ data }) => data),
    fetchAccount: (token, accountId) =>
        axios
            .get(`crud/funded-accounts/${accountId}`, { headers: { token } })
            .then((response) => {
                let responseData = null;

                if (response.status === 200) {
                    responseData = FORMAT.INDIVIDUAL_ACCOUNT_ADMIN_BEAUTIFY(response.data);
                }

                return responseData;
            })
            .catch((error) => {
                console.error('Error:', error);
                return null;
            })
};

// Helper to get auth token for TRADEBOOK
const getTradebookAuthToken = () => {
    const { user } = userStore.getState();
    const { viewAs } = useManagementStore.getState();

    const isViewAsActive =
        JSON.stringify(viewAs?.newUser?.token || null) !==
        JSON.stringify(viewAs?.mainUser?.token || null);

    return isViewAsActive ? viewAs.newUser.token : user?.token;
};

// Helper to create auth headers
const getTradebookAuthHeaders = () => ({
    token: getTradebookAuthToken(),
    'Content-Type': 'application/json'
});

export const TRADEBOOK = {
    // TRADING PLANS
    fetchTradingPlans: () =>
        axios
            .get('/tradebook/trading-plan', {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchTradingPlan: (planId) =>
        axios
            .get(`/tradebook/trading-plan/${planId}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    createTradingPlan: (planData) =>
        axios
            .post('/tradebook/trading-plan', planData, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    updateTradingPlan: (planId, updates) =>
        axios
            .put(`/tradebook/trading-plan/${planId}`, updates, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    deleteTradingPlan: (planId) =>
        axios.delete(`/tradebook/trading-plan/${planId}`, {
            headers: getTradebookAuthHeaders()
        }),

    reorderTradingPlans: (planIds) =>
        axios.post(
            '/tradebook/trading-plan/reorder',
            { planIds },
            {
                headers: getTradebookAuthHeaders()
            }
        ),

    // TRADEBOOK BREAKDOWN
    fetchBreakdown: (planId) =>
        axios
            .get(`/tradebook/breakdown/${planId}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    updateBreakdown: (planId, updates) =>
        axios
            .put(`/tradebook/breakdown/${planId}`, updates, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    // TRADEBOOK GLOBAL
    fetchGlobal: () =>
        axios
            .get('/tradebook/global', {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    updateGlobal: (updates) =>
        axios
            .put('/tradebook/global', updates, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    // STRATEGY STATISTICS
    fetchAllStats: () =>
        axios
            .get('/tradebook/strategy-stats', {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchStrategyStats: (planId) =>
        axios
            .get(`/tradebook/strategy-stats/${planId}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    // TRADE ASSIGNMENT
    assignTradesToStrategy: (tradeIds, planId) =>
        axios
            .post(
                '/tradebook/assign-trades',
                { tradeIds, planId },
                { headers: getTradebookAuthHeaders() }
            )
            .then(({ data }) => data),

    unassignTrades: (tradeIds) =>
        axios
            .post(
                '/tradebook/unassign-trades',
                { tradeIds },
                { headers: getTradebookAuthHeaders() }
            )
            .then(({ data }) => data),

    // TRADE EVALUATION
    fetchPlanCriteria: (planId) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/criteria`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchTradeEvaluation: (tradeId) =>
        axios
            .get(`/tradebook/evaluation/${tradeId}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    saveTradeEvaluation: (tradeId, evaluationData) =>
        axios
            .put(`/tradebook/evaluation/${tradeId}`, evaluationData, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    clearTradeEvaluation: (tradeId) =>
        axios
            .delete(`/tradebook/evaluation/${tradeId}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    // CRITERIA STATISTICS
    fetchCriteriaStats: (planId) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/criteria-stats`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data?.data || data),

    // STRATEGY SETS
    fetchStrategySetsForPlan: (planId) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/strategy-sets`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchSetsByTypeForPlan: (planId, setType) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/strategy-sets/${setType}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchSetDetails: (planId, setType, setNumber) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/strategy-sets/${setType}/${setNumber}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    getSetTradeCount: (planId, setType, setNumber) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/strategy-sets/${setType}/${setNumber}/count`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    validateSetAssignment: (data) =>
        axios
            .post('/tradebook/strategy-sets/validate', data, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data: responseData }) => responseData),

    // PROFITABILITY COMPARISON
    fetchRMultipleAnalysis: (planId, setType, setNumber) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/profitability/${setType}/${setNumber}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchProfitabilitySummary: (planId) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/profitability-summary`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    simulatePartialStrategies: (planId, simulationData) =>
        axios
            .post(`/tradebook/trading-plan/${planId}/simulate-partials`, simulationData, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    // STRATEGY SET COMMENTS
    fetchAllSetComments: (planId) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/set-comments`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    fetchSetComment: (planId, setType, setNumber) =>
        axios
            .get(`/tradebook/trading-plan/${planId}/set-comments/${setType}/${setNumber}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    updateSetComment: (planId, setType, setNumber, comment) =>
        axios
            .put(
                `/tradebook/trading-plan/${planId}/set-comments/${setType}/${setNumber}`,
                { comment },
                { headers: getTradebookAuthHeaders() }
            )
            .then(({ data }) => data),

    deleteSetComment: (planId, setType, setNumber) =>
        axios
            .delete(`/tradebook/trading-plan/${planId}/set-comments/${setType}/${setNumber}`, {
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data),

    // AVAILABLE STRATEGY SETS (for trade filtering)
    fetchAvailableStrategySets: (tradingPlanId, setType = null) =>
        axios
            .get(`/crud/trades/strategy-sets/${tradingPlanId}`, {
                params: setType ? { setType } : {},
                headers: getTradebookAuthHeaders()
            })
            .then(({ data }) => data)
            .catch((error) => {
                console.error('Error fetching strategy sets:', error);
                return { strategy: [], noStrategy: [] };
            })
};

export const DISCIPLINE = {
    fetchDisciplineStats: (filter) =>
        axios
            .get('stats/discipline', {
                params: {
                    accounts: filter.accounts,
                    assetType: filter.assetType,
                    tags: filter.tags,
                    showAllTags: filter.showAllTags,
                    symbols: filter.symbols,
                    status: filter.status,
                    side: filter.side,
                    startDate: filter.dateRange
                        ? filter.dateRange[0].toString()
                        : dayjs()
                              .add(-20, 'y')
                              .set('hour', 0)
                              .set('minute', 0)
                              .set('second', 0)
                              .set('millisecond', 0)
                              .toDate(),
                    endDate: filter.dateRange
                        ? filter.dateRange[1].toString()
                        : dayjs()
                              .add(20, 'y')
                              .set('hour', 23)
                              .set('minute', 59)
                              .set('second', 59)
                              .set('millisecond', 59)
                              .toDate(),
                    getAllofTickers: filter.getAllofTickers
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
            })
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }

                return null;
            })
            .catch((error) => {
                console.error('Error fetching discipline stats:', error);
                return null;
            }),

    fetchPerformanceGap: (filter) =>
        axios
            .get('stats/performance-gap', {
                params: {
                    accounts: filter.accounts,
                    assetType: filter.assetType,
                    tags: filter.tags,
                    showAllTags: filter.showAllTags,
                    symbols: filter.symbols,
                    status: filter.status,
                    side: filter.side,
                    startDate: filter.dateRange
                        ? filter.dateRange[0].toString()
                        : dayjs()
                              .add(-20, 'y')
                              .set('hour', 0)
                              .set('minute', 0)
                              .set('second', 0)
                              .set('millisecond', 0)
                              .toDate(),
                    endDate: filter.dateRange
                        ? filter.dateRange[1].toString()
                        : dayjs()
                              .add(20, 'y')
                              .set('hour', 23)
                              .set('minute', 59)
                              .set('second', 59)
                              .set('millisecond', 59)
                              .toDate(),
                    getAllofTickers: filter.getAllofTickers
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' })
            })
            .then((response) => {
                if (response.status === 200) {
                    return response.data;
                }

                return null;
            })
            .catch((error) => {
                console.error('Error fetching performance gap:', error);
                return null;
            })
};
